import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { QuickDrawer } from 'shared/components';
import { useFormik } from 'formik';
import numeral from 'numeral';
import { isEqual, uniq, cloneDeep } from 'lodash';
import Autocomplete from '@mui/lab/Autocomplete';
import { ConfirmationDialog } from 'shared/components';
//import { sortObject } from '../../utils'
import { setOpenDrawer } from '../../actions';
import DueDate from '../DueDate';
import VariableTimeline from '../VariableTimeline';
import {
  TextField,
  Checkbox,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormHelperText,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Link,
  Fade,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Done as DoneIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  YEAR_CHOICES_PLACEHOLDER,
  FEEDBACK_TYPE_PLACEHOLDER,
  DEFAULT_PORTFOLIO_STATUS,
  TIMELINE_CHOICES,
  validationSchema,
  getServicesName,
  yearsChoices,
} from './utils';
import {
  Container,
  Header,
  Body,
  Row,
  Col,
  ColButton,
  TableBig,
  TRow,
  TCol,
  Button,
  OptionText,
  Box,
  Br,
  Popper,
  Icon,
  SaveChip,
  ErrorChip,
} from './styles';
import {
  useDeletePortfolioMutation,
  useNewPortfolioMutation,
  usePortfolioById,
  useUpdatePortfolioMutation,
  useAllUsers,
  usePortfolioSelect,
} from 'shared/apollo';
import { useUser } from 'shared/auth';
import { formattedDate } from 'shared/utils/date';

const truncate = input => {
  if (input?.length > 20) {
    return input.substring(0, 20) + '...';
  }
  return input;
};

const initialValues = {
  name: '',
  description: '',
  years: [],
  services: [],
  isImpactSnapshot: false,
};

const PortfolioDrawer = ({
  open,
  onClose,
  servicesList,
  portfolioId,
  refresh,
  portfoliosList,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [completed, setCompleted] = React.useState(false);
  const [openDatePopper, setOpenDatePopper] = React.useState(false);
  const [placement, setPlacement] = React.useState();
  const [expanded, setExpanded] = React.useState('fixedDate');
  const [yearChoices, setYearChoice] = useState([
    YEAR_CHOICES_PLACEHOLDER,
    ...yearsChoices,
  ]);
  const [selectedYear, setSelectedYear] = useState(YEAR_CHOICES_PLACEHOLDER);
  const [popperYearIndex, setPopperYearIndex] = useState();
  const [popperDateNumber, setPopperDateNumber] = useState();
  const [popperTitle, setPopperTitle] = useState();
  const [currentPortfolioId, setCurrentPortfolioId] = useState();
  const [deleteDialog, setDeleteDialog] = useState(false);

  const servicesName = getServicesName(servicesList);
  const { run: runNew, isLoading: loadingNew } = useNewPortfolioMutation();
  const {
    run: runUpdate,
    isLoading: loadingUpdate,
  } = useUpdatePortfolioMutation();
  const {
    run: runDelete,
    isLoading: loadingDelete,
  } = useDeletePortfolioMutation();
  const { portfolio, refresh: refreshPortfolio } = usePortfolioById(
    currentPortfolioId || portfolioId,
  );
  const { refresh: refreshPortfolioSelect } = usePortfolioSelect();
  const { data: managersList } = useAllUsers();
  const user = useUser();
  const editPortfolioId = currentPortfolioId || portfolioId;
  const [addYearOpen, setAddYearOpen] = useState(false);
  const [dialogDismiss, setDialogDismiss] = useState(false);
  const userInSessionId = user?.effectiveUser?.attributes?.['custom:user_id'];
  const [startValues, setStartValues] = useState(initialValues);
  const [requestError, setRequestError] = useState(undefined);

  const currentNames = uniq(
    portfoliosList
      .filter(
        ({ id, name }) => id !== editPortfolioId && name !== portfolio?.name,
      )
      .map(({ name }) => name.toLowerCase()),
  );

  const onUpdateCompleted = () => {
    setRequestError(undefined);
    refresh();
    refreshPortfolioSelect();
    setCompleted(true);
    refreshPortfolio();
    setTimeout(() => {
      setCompleted(false);
    }, 2500);
  };

  const onNewCompleted = id => {
    setRequestError(undefined);
    refresh();
    refreshPortfolioSelect();
    setCompleted(true);
    setCurrentPortfolioId(id);
    refreshPortfolio();
    setTimeout(() => {
      setCompleted(false);
    }, 2500);
  };

  const runSubmit = async values => {
    setRequestError(undefined);
    const { name, description, years, isImpactSnapshot } = values;
    setStartValues(cloneDeep(values));
    const formatedYears = years.map(({ year, type, ...more }) => ({
      ...more,
      year,
      ReviewLevel: type !== FEEDBACK_TYPE_PLACEHOLDER ? type : null,
      services: values.services.map(
        service => servicesList.find(item => item.name === service).id,
      ),
    }));
    const form = {
      status: DEFAULT_PORTFOLIO_STATUS,
      name,
      description,
      years: JSON.stringify(formatedYears),
      isImpactSnapshot: isImpactSnapshot ?? false,
    };
    if (editPortfolioId) {
      runUpdate({ ...form, id: editPortfolioId }, onUpdateCompleted, () => {
        setRequestError('That portfolio name is in use.');
      });
    } else {
      runNew(form, onNewCompleted, () => {
        setRequestError('That portfolio name is in use.');
      });
    }
    //const formNewValues = formNewValuesParser();
  };

  const { values, handleSubmit, setFieldValue, errors, dirty } = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues,
    validate: values => {
      return validationSchema(values, currentNames);
    },
    onSubmit: runSubmit,
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (portfolio) {
      setFieldValue('name', portfolio.name);
      setFieldValue('description', portfolio.description);
      setFieldValue('isImpactSnapshot', portfolio.isImpactSnapshot ?? false);

      const years = portfolio.years.map(year => ({
        ...year,
        type: year.reviewLevel,
        services: year.services.map(
          id => servicesList.find(item => item.id === id)?.name,
        ),
        manager: year?.manager ?? userInSessionId,
      }));

      setFieldValue('years', years);
      const updatedYearChoices = [...yearChoices].reduce((acc, item) => {
        const yearInUse =
          portfolio.years.filter(p => p.year === item).length > 0;
        if (!yearInUse) acc.push(item);
        return acc;
      }, []);

      const updatedValues = {
        ...values,
        name: portfolio.name,
        description: portfolio.description,
        years,
      };

      if (portfolio.years && portfolio.years.length > 0) {
        const services = portfolio.years[0].services.map(
          id => servicesList.filter(item => item.id === id)[0]?.name,
        );
        setFieldValue(
          'services',
          services.filter(ser => !!ser),
        );
        updatedValues.services = services;
      }
      setYearChoice(updatedYearChoices);
      setStartValues(cloneDeep(updatedValues));
    }
  }, [portfolio]);

  useEffect(() => {
    dispatch(setOpenDrawer('portfolio', dirty));
  }, [dirty]);

  useEffect(() => {
    return () => {
      dispatch(setOpenDrawer('portfolio', false));
    };
  }, []);

  const handleChangeAccordion = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleDatePopper = (
    newPlacement,
    dateNumber,
    popperTitle,
    yearIndex,
  ) => event => {
    setAnchorEl(event.currentTarget);
    setPopperTitle(popperTitle);
    setOpenDatePopper(prev => placement !== newPlacement || !prev);
    setPlacement(newPlacement);
    setPopperYearIndex(yearIndex);
    setPopperDateNumber(dateNumber);
  };

  const headers = ['Summary'];
  const overviewRows = [
    { overview: 'Investment count' },
    { overview: 'Program count' },
    { overview: 'Report count' },
    { overview: '$Invested' },
    { overview: '$Allocated' },
  ];

  if (portfolio && portfolio.years) {
    portfolio.years.forEach(item => {
      headers.push(item.year.toString());
      overviewRows[0][item.year.toString()] = item.investments;
      overviewRows[1][item.year.toString()] = item.programs;
      overviewRows[2][item.year.toString()] = item.reports;
      overviewRows[3][item.year.toString()] = `$${numeral(item.invested).format(
        0,
        0,
      )}`;
      overviewRows[4][item.year.toString()] = `$${numeral(
        item.allocated,
      ).format(0, 0)}`;
    });
  }

  const feedbackType = [
    { id: FEEDBACK_TYPE_PLACEHOLDER, name: FEEDBACK_TYPE_PLACEHOLDER },
    { id: 'All', name: 'Full (TI + Donor)' },
    { id: 'Grantee+TI', name: 'TI Only' },
    { id: 'Grantee+Funder', name: 'Donor Only' },
    { id: 'Grantee', name: 'No Feedback' },
  ];

  const handleYearChoicesChangeAndSave = e => {
    setSelectedYear(e.target.value);
    const updatedYears = [...values.years];
    const updatedYearChoices = [...yearChoices].filter(
      year => year !== e.target.value,
    );
    updatedYears.push({
      year: e.target.value,
      services: [],
      type: FEEDBACK_TYPE_PLACEHOLDER,
      manager: userInSessionId,
    });
    setFieldValue('years', updatedYears);
    setYearChoice(updatedYearChoices);
    setSelectedYear(YEAR_CHOICES_PLACEHOLDER);
    setAddYearOpen(false);
  };

  const deleteYear = yearToDelete => {
    const updatedYears = [...values.years].filter(
      year => year.year !== yearToDelete,
    );
    setFieldValue('years', updatedYears);

    const updatedYearChoices = [...yearChoices];
    updatedYearChoices.push(yearToDelete);
    updatedYearChoices.sort(function(a, b) {
      return b - a;
    });
    setYearChoice(updatedYearChoices);
  };

  /*const handleChangeServiceAutocomplete = (index, value) => {
    const updatedYears = [...values.years];
    updatedYears[index].services = value;
    setFieldValue("years", updatedYears);
  }*/

  const handleChangeFeedbackType = (index, value) => {
    const updatedYears = [...values.years];
    updatedYears[index].type = value;
    setFieldValue('years', updatedYears);
  };

  const handleChangeManager = (index, value) => {
    const updatedYears = [...values.years];
    updatedYears[index].manager = value;
    setFieldValue('years', updatedYears);
  };

  const parseDate = (year, date) => {
    return (
      formattedDate(year[`report_date_${date}`]) ||
      (year[`timeline_from_${date}`] && year[`timeline_days_${date}`]
        ? TIMELINE_CHOICES.filter(
            TC => TC.id === year[`timeline_from_${date}`],
          )[0].name +
          ' + ' +
          year[`timeline_days_${date}`] +
          ' days'
        : 'No Deadline')
    );
  };

  const getYearDateRows = year => {
    return [
      { name: 'Allocate Investment', date: parseDate(year, 1), number: 1 },
      { name: 'Submit For Feedback', date: parseDate(year, 2), number: 2 },
      { name: 'Resolve Feedback', date: parseDate(year, 3), number: 3 },
      { name: 'Publish Report ', date: parseDate(year, 4), number: 4 },
      { name: 'Interim Report Update', date: parseDate(year, 5), number: 5 },
      { name: 'Finalize Report ', date: parseDate(year, 6), number: 6 },
    ];
  };

  const onChangeTimelineDate = value => {
    const updatedYears = [...values.years];
    updatedYears[popperYearIndex][`timeline_days_${popperDateNumber}`] = null;
    updatedYears[popperYearIndex][`timeline_from_${popperDateNumber}`] = null;
    updatedYears[popperYearIndex][`report_date_${popperDateNumber}`] = value;
    setFieldValue('years', updatedYears);
  };

  const onChangeTimelineDays = value => {
    const updatedYears = [...values.years];
    updatedYears[popperYearIndex][`report_date_${popperDateNumber}`] = null;
    updatedYears[popperYearIndex][`timeline_days_${popperDateNumber}`] = value;
    setFieldValue('years', updatedYears);
  };

  const onChangeTimelineFrom = value => {
    const updatedYears = [...values.years];
    updatedYears[popperYearIndex][`report_date_${popperDateNumber}`] = null;
    updatedYears[popperYearIndex][`timeline_from_${popperDateNumber}`] = value;
    setFieldValue('years', updatedYears);
  };

  const onSaveTimeline = () => {
    setAnchorEl();
    setPlacement();
    setPopperYearIndex();
    setPopperDateNumber();
  };

  const onFinishDelete = () => {
    setCurrentPortfolioId();
    setFieldValue('name', '');
    setFieldValue('description', '');
    setFieldValue('years', []);
    refresh();
    refreshPortfolioSelect();
    onClose();
  };

  const handleDelete = () => {
    setDeleteDialog(false);
    runDelete({ id: editPortfolioId }, onFinishDelete);
  };

  /*const formNewValuesParser = () => {
    const { name, description, years, ...more } = values;
    const formatedYears = years.map(({ year, services, type, ...more }) => ({
      ...more,
      year,
      ReviewLevel: (type !== FEEDBACK_TYPE_PLACEHOLDER) ? type : null,
      services: services.map(service => servicesList.filter(item => item.name === service)[0].id)
    }))
    formatedYears[0].services = values.services.map(service => servicesList.filter(item => item.name === service)[0].id)
    const formNewValues = {
      name,
      portfolioId: portfolioId,
      description,
      status: DEFAULT_PORTFOLIO_STATUS.toUpperCase(),
      years: formatedYears,
    }
    formNewValues.years.forEach((year, index) => {
      formNewValues.years[index] = sortObject(formNewValues.years[index])
      delete formNewValues.years[index].ReviewLevel
    });
    return formNewValues;
  }*/

  const hasChanged = !isEqual(values, startValues);

  const handleDismiss = () => {
    if (hasChanged) {
      setDialogDismiss(true);
    } else {
      onClose();
    }
  };

  const handleCancelDismiss = () => {
    setDialogDismiss(false);
  };

  const handleConfirmDismiss = () => {
    onClose();
  };

  const summary = useMemo(() => {
    const data = {
      investments: 0,
      programs: 0,
      reports: 0,
      invested: '$0,00',
      allocated: '$0,00',
    };
    const year = headers?.[1];
    if (year) {
      data.year = year;
      data.investments = overviewRows?.[0][year] || 0;
      data.programs = overviewRows?.[1][year] || 0;
      data.reports = overviewRows?.[2][year] || 0;
      data.invested = overviewRows?.[3][year] || '$0,00';
      data.allocated = overviewRows?.[4][year] || '$0,00';
    }
    return data;
  }, [headers, overviewRows]);

  const saveEnabled =
    !!(
      values.name &&
      values.years &&
      values.years.length > 0 &&
      values.years.every(
        year => year.type && year.type !== FEEDBACK_TYPE_PLACEHOLDER,
      )
    ) || hasChanged;

  const isLoadingDataForEdition = portfolioId && !values.name;
  const isLoading =
    loadingNew || loadingUpdate || loadingDelete || isLoadingDataForEdition;
  const hasInvestment = (portfolio?.investments || 0) > 0;

  return (
    <QuickDrawer open={open} onClose={handleDismiss} width="432px;" title="">
      {popperYearIndex !== undefined && (
        <div>
          <Popper
            open={openDatePopper}
            anchorEl={anchorEl}
            placement="top-end"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <div>
                  <div className="p-header">
                    <span className="h-title">{`Select ${popperTitle} due date`}</span>
                    <Icon>
                      <CloseIcon onClick={() => setPopperYearIndex()} />
                    </Icon>
                  </div>
                  <Accordion
                    expanded={expanded === 'fixedDate'}
                    onChange={handleChangeAccordion('fixedDate')}
                    style={{ textAlign: 'center' }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1bh-content"
                      id="panel1bh-header"
                    >
                      <span className="h-title">Fixed Date</span>
                    </AccordionSummary>
                    <AccordionDetails>
                      <DueDate
                        year={values.years?.[popperYearIndex]}
                        number={popperDateNumber}
                        onChangeTimelineDate={onChangeTimelineDate}
                        onSave={onSaveTimeline}
                      />
                    </AccordionDetails>
                  </Accordion>
                  <Accordion
                    expanded={expanded === 'variableTimeline'}
                    onChange={handleChangeAccordion('variableTimeline')}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel2bh-content"
                      id="panel2bh-header"
                    >
                      <span className="h-title">Variable Timeline</span>
                    </AccordionSummary>
                    <AccordionDetails>
                      <VariableTimeline
                        options={TIMELINE_CHOICES}
                        year={values.years?.[popperYearIndex]}
                        number={popperDateNumber}
                        onChangeTimelineDays={onChangeTimelineDays}
                        onChangeTimelineFrom={onChangeTimelineFrom}
                        onSave={onSaveTimeline}
                      />
                    </AccordionDetails>
                  </Accordion>
                </div>
              </Fade>
            )}
          </Popper>
        </div>
      )}
      <Container>
        <Header>
          <Row className="mb15">
            <Col>
              <h2>Portfolio</h2>
            </Col>
            {summary.year && (
              <Col flexEnd>
                <span className="h-price">({summary.year})</span>
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              <span className="h-title">{values.name || 'New Portfolio'}</span>
            </Col>
          </Row>
          <Row className="mb15">
            <Col>
              <span className="h-sub">
                Invested: {summary.invested} | Allocated: {summary.allocated}
              </span>
            </Col>
          </Row>
          <Row>
            <TableBig>
              <TRow>
                <TCol style={{ borderLeft: 'none' }}>
                  <span className="r-title">Investment Count:</span>
                  <span className="r-sub">{summary.investments}</span>
                </TCol>
              </TRow>
              <TRow>
                <TCol>
                  <span className="r-title">Program Count:</span>
                  <span className="r-sub">
                    {summary.programs || portfolio?.allocations || 0}
                  </span>
                </TCol>
                <TCol>
                  <span className="r-title">Report Count:</span>
                  <span className="r-sub">{summary.reports}</span>
                </TCol>
              </TRow>
            </TableBig>
          </Row>
        </Header>
        <Body>
          <Row>
            <Col>
              <span className="r-title">Name:</span>
            </Col>
          </Row>
          <Row className="mb15">
            <Col>
              <TextField
                placeholder="Name"
                variant="outlined"
                size="small"
                value={values.name}
                onChange={e => setFieldValue('name', e.target.value)}
                error={errors.name}
                helperText={errors.name}
                fullWidth
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <span className="r-title">Description:</span>
            </Col>
          </Row>
          <Row className="mb15">
            <Col>
              <TextField
                className="textarea"
                placeholder="Description"
                multiline
                rows={2}
                variant="outlined"
                size="small"
                value={values.description}
                onChange={e => setFieldValue('description', e.target.value)}
                error={errors.description}
                helperText={errors.description}
                fullWidth
              />
            </Col>
          </Row>
          <Row className="mb15">
            <Col>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="r-title">Impact Snapshot:</span>
                <Checkbox
                  checked={values.isImpactSnapshot ?? false}
                  onChange={e => setFieldValue('isImpactSnapshot', e.target.checked)}
                  size="small"
                  disabled={!!editPortfolioId}
                  style={{ padding: 0 }}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <span className="r-title">Impact Model:</span>
            </Col>
          </Row>
          <Row className="mb15">
            <Col>
              <Autocomplete
                multiple
                className="autocomplete"
                size="small"
                options={servicesName}
                disableCloseOnSelect
                getOptionLabel={option => truncate(option)}
                value={values.services}
                onChange={(_, value) => setFieldValue('services', value)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                    />
                    <OptionText>{option}</OptionText>
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    fullWidth
                    {...params}
                    variant="outlined"
                    placeholder={'Impact model'}
                  />
                )}
              />
            </Col>
          </Row>
          <Box>
            {values.years.map(({ year, type, manager, ...more }, index) => (
              <Accordion
                key={year}
                onChange={(_, expanded) => {
                  if (!expanded) {
                    setPopperYearIndex();
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <span className="r-title">{year}</span>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: 'column' }}>
                  <Row>
                    <Col>
                      <span className="r-title">Feedback Type:</span>
                    </Col>
                  </Row>
                  <Row className="mb15">
                    <Col>
                      <Select
                        size="small"
                        value={type}
                        onChange={e =>
                          handleChangeFeedbackType(index, e.target.value)
                        }
                        inputProps={{ id: 'feedback-type' }}
                      >
                        {feedbackType.map(item => (
                          <MenuItem key={item} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <span className="r-title">Manager:</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Select
                        size="small"
                        value={manager}
                        onChange={e =>
                          handleChangeManager(index, e.target.value)
                        }
                        inputProps={{
                          id: 'manager-selector',
                        }}
                      >
                        {managersList.map(item => (
                          <MenuItem
                            key={item.dbId}
                            value={item.dbId}
                          >{`${item.firstName} ${item.lastName}`}</MenuItem>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                  {/* TIMELINE */}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Reporting dates</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getYearDateRows(more).map(row => (
                          <TableRow key={row.name}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                              <Link
                                onClick={handleDatePopper(
                                  'left',
                                  row.number,
                                  row.name,
                                  index,
                                )}
                              >
                                {row.date}
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Br />
                  <Button
                    size="large"
                    color="primary"
                    startIcon={<DeleteIcon />}
                    onClick={() => deleteYear(year)}
                    className="delete-button"
                  >
                    Delete
                  </Button>
                  <Br />
                </AccordionDetails>
              </Accordion>
            ))}
            <Collapse in={addYearOpen}>
              <Select
                size="small"
                style={{ marginBottom: '10px' }}
                value={selectedYear}
                onChange={handleYearChoicesChangeAndSave}
              >
                {yearChoices.map(item => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </Collapse>
            <FormHelperText error>{errors.years}</FormHelperText>
            <Button
              size="large"
              color="primary"
              startIcon={addYearOpen ? <CloseIcon /> : <AddIcon />}
              onClick={() => setAddYearOpen(!addYearOpen)}
            >
              {addYearOpen ? 'Cancel' : 'Add year'}
            </Button>
          </Box>
        </Body>
        <Body className="buttons">
          <Row>
            <Col>
              {isLoading && (
                <LinearProgress
                  style={{ marginBottom: '15px' }}
                  variant="query"
                />
              )}
            </Col>
          </Row>
          {completed && (
            <Row>
              <SaveChip>
                <span>Portfolio Saved</span>
                <DoneIcon />
              </SaveChip>
            </Row>
          )}
          {requestError && (
            <Row>
              <ErrorChip>
                <span>{requestError}</span>
                <ErrorIcon />
              </ErrorChip>
            </Row>
          )}
          <Row>
            <ColButton>
              <Button
                disabled={!saveEnabled}
                onClick={handleSubmit}
                size="large"
                variant="contained"
                color="primary"
              >
                Save
              </Button>
            </ColButton>
            <ColButton>
              <Button
                onClick={() => setDeleteDialog(true)}
                disabled={hasInvestment || !editPortfolioId}
                color="primary"
                size="large"
                variant="outlined"
              >
                Delete
              </Button>
            </ColButton>
            <ColButton>
              <Button
                onClick={handleDismiss}
                size="large"
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
            </ColButton>
          </Row>
        </Body>
      </Container>
      <ConfirmationDialog
        openDialog={deleteDialog}
        onCloseDialog={() => setDeleteDialog(false)}
        actionButtonOne={handleDelete}
        actionButtonTwo={() => setDeleteDialog(false)}
        textButtonOne="Delete"
        textButtonTwo="Cancel"
        title="Are you sure you want to delete this portfolio?"
      />
      <ConfirmationDialog
        openDialog={dialogDismiss}
        onCloseDialog={handleCancelDismiss}
        actionButtonOne={handleConfirmDismiss}
        actionButtonTwo={handleCancelDismiss}
        textButtonOne="Confirm"
        textButtonTwo="Cancel"
        title="You have made some changes to this portfolio, any unsaved changes will be lost."
      />
    </QuickDrawer>
  );
};

export default PortfolioDrawer;
